// src/app/components/foundation/WorkspaceActions.tsx
import type { HTMLAttributes, ReactNode } from 'react';
import clsx from 'clsx';

export interface WorkspaceActionsProps extends HTMLAttributes<HTMLDivElement> {
  children: ReactNode;
  className?: string;
}

/**
 * Shared action-bar shell for a `Workspace` panel. Renders a bottom-anchored,
 * RTL-aware compact admin toolbar (buttons/links) with the same hairline-
 * border and token-driven surface treatment as `Card` / `Workspace` /
 * `WorkspaceSection`.
 *
 * Children lay out horizontally, RTL-first, as a compact admin-style
 * toolbar -- `[ icon + label ]  [ icon + label ]  [ icon + label ]` -- not
 * a stack of large primary CTA buttons. Actions stay on a single row at
 * every screen size, including narrow mobile: buttons shrink to fit
 * instead of wrapping onto a second line or triggering horizontal
 * scrolling. Balanced padding on both physical edges of the container
 * keeps the last action from sitting flush against the edge on tablet.
 * Per-feature Actions components render buttons only -- no layout of
 * their own -- so the toolbar look is normalized entirely from this
 * shared wrapper via a descendant selector targeting any `button` inside.
 * This is the single owner of footer layout, RTL direction, spacing,
 * wrapping behavior, button sizing, typography, and disabled appearance;
 * only each variant's tint (background/border/text color while enabled)
 * is left to the feature's own action item, so primary/neutral/danger
 * colors and click handlers keep working exactly as defined there.
 *
 * Purely presentational -- callers own everything inside (`children`): no
 * business logic, no routing, no permissions, no API calls.
 */
export function WorkspaceActions({ children, className, ...rest }: WorkspaceActionsProps) {
  return (
    <div
      dir="rtl"
      className={clsx(
        'flex w-full min-w-0 flex-nowrap items-center gap-1 border-t border-[var(--vania-colors-border-subtle,rgba(255,255,255,0.08))] px-3 py-1',
        // Compact sizing + typography + disabled appearance, applied to
        // every button a feature Actions component renders -- height,
        // padding, font-size, font-weight, radius, shadow, and the muted
        // disabled text color. Enabled background/border/text tint is
        // intentionally left untouched so each feature's own
        // primary/neutral/danger colors keep showing through.
        '[&_button]:h-6 [&_button]:min-w-0 [&_button]:max-w-full [&_button]:shrink [&_button]:basis-auto [&_button]:overflow-hidden [&_button]:text-ellipsis [&_button]:whitespace-nowrap [&_button]:px-2 [&_button]:py-0 [&_button]:text-[11px] [&_button]:font-normal [&_button]:leading-[normal] [&_button]:rounded-[var(--vania-radius-sm,6px)] [&_button]:gap-0.5 [&_button]:shadow-none',
        '[&_button:disabled]:!text-[var(--vania-colors-text-muted,#8b93a1)] [&_button:disabled]:cursor-not-allowed',
        className,
      )}
      {...rest}
    >
      {children}
    </div>
  );
}
